import api from "shared/api";
import { CONNECTION_TYPE } from "../../../member/sections/Connection/constants";

const FETCH_REQUEST = "consolidatedConnectionList/FETCH_REQUEST";
const FETCH_SUCCESS = "consolidatedConnectionList/FETCH_SUCCESS";
const FETCH_FAILURE = "consolidatedConnectionList/FETCH_FAILURE";
const CLEAR_DATA = "consolidatedConnectionList/CLEAR_DATA";

const initialState = {
  result: { connection_list: [] },
  loaded: false,
  loading: false,
  errors: [],
};

export function fetchRequest() {
  return { type: FETCH_REQUEST };
}

export function fetchSuccess(payload) {
  return { type: FETCH_SUCCESS, payload };
}

export function fetchFailure(errors = []) {
  return { type: FETCH_FAILURE, errors };
}

export function clearData() {
  return { type: CLEAR_DATA };
}

export function loadConsolidatedList({ id, role }) {
  return async (dispatch) => {
    dispatch(fetchRequest());

    // --- Case 1: Role is "both" ---
    // Fetch both director and executive lists.
    // Use Promise.allSettled so that if one fails, we can still use the other.
    if (role === CONNECTION_TYPE.BOTH) {
      try {
        const [dirResult, execResult] = await Promise.allSettled([
          api(`/api/v1/directors/${id}/connection_list`),
          api(`/api/v1/executives/${id}/connection_list`),
        ]);

        let dirs = [];
        let execs = [];

        // Handle director result
        if (dirResult.status === "fulfilled" && dirResult.value.ok) {
          const dirJson = await dirResult.value.json();
          if (Array.isArray(dirJson)) {
            dirs = dirJson.map((item) => ({
              ...item,
              connection_type: "director",
            }));
          }
        } else {
          console.error(
            "Director API call failed:",
            dirResult.reason || dirResult.value
          );
        }

        // Handle executive result
        if (execResult.status === "fulfilled" && execResult.value.ok) {
          const execJson = await execResult.value.json();
          if (Array.isArray(execJson)) {
            execs = execJson.map((item) => ({
              ...item,
              connection_type: "executive",
            }));
          }
        } else {
          console.error(
            "Executive API call failed:",
            execResult.reason || execResult.value
          );
        }

        // If both calls failed to produce any data, then dispatch a failure.
        if (dirs.length === 0 && execs.length === 0) {
          throw new Error(
            "Both director and executive connection lists failed to load."
          );
        }

        // Merge and dispatch whatever data was successfully fetched
        const merged = [...dirs, ...execs];
        dispatch(fetchSuccess({ connection_list: merged }));
      } catch (err) {
        dispatch(fetchFailure([err.message]));
      }
      return;
    }

    // --- Case 2: Role is "executive" ---
    // Fetch only the executive list.
    if (role === CONNECTION_TYPE.EXECUTIVE) {
      try {
        const res = await api(`/api/v1/executives/${id}/connection_list`);
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        const json = await res.json();
        const execs = json.map((item) => ({
          ...item,
          connection_type: "executive",
        }));
        dispatch(fetchSuccess({ connection_list: execs }));
      } catch (err) {
        dispatch(fetchFailure([err.message]));
      }
      return;
    }

    // --- Case 3: Role is "board_member" or any other case (Fallback) ---
    // Fetch only the director list. This handles BOARD_MEMBER and is a safe default.
    try {
      const res = await api(`/api/v1/directors/${id}/connection_list`);
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();
      const dirs = json.map((item) => ({
        ...item,
        connection_type: "director",
      }));
      dispatch(fetchSuccess({ connection_list: dirs }));
    } catch (err) {
      dispatch(fetchFailure([err.message]));
    }
  };
}

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case FETCH_REQUEST:
      return { ...state, loading: true };
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
        result: action.payload,
      };
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      };
    case CLEAR_DATA:
      return initialState;
    default:
      return state;
  }
}
